import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import RichText from "../RichText";
import { useEditor } from "@tiptap/react";

import {
  getSharedEditorExtensions,
  getSharedEditorProps,
} from "@/components/documents/editorExtensions";

interface Document {
  id: string;
  [key: string]: any;
}

interface DocumentEditorProps {
  doc: Document;
  value: string;
  onChange?: (value: string) => void;
  onFileUpload?: (file: File) => Promise<string>;
  readOnly?: boolean;
  renderToolbar?: boolean;
  onInitialClick?: () => void;
}

const DocumentEditor = forwardRef<{ editor: any }, DocumentEditorProps>(
  (
    {
      doc,
      value,
      onChange,
      onFileUpload,
      readOnly = false,
      renderToolbar = false,
      onInitialClick,
    },
    ref,
  ) => {
    const [textInput, setTextInput] = useState(value || "");

    const extensions = useMemo(() => getSharedEditorExtensions(), []);
    const editorProps = useMemo(
      () => getSharedEditorProps({ onFileUpload }),
      [onFileUpload],
    );

    const editor = useEditor({
      immediatelyRender: false,
      shouldRerenderOnTransaction: false,
      extensions,
      content: textInput,
      editable: !readOnly,
      onUpdate: ({ editor }) => {
        const html = editor.getHTML();
        setTextInput(html);
        if (onChange) {
          onChange(html);
        }
      },
      editorProps,
    });

    useImperativeHandle(ref, () => ({
      editor,
    }));

    useEffect(() => {
      if (editor && value !== editor.getHTML()) {
        editor.commands.setContent(value);
        setTextInput(value);
      }
    }, [value, editor]);

    return (
      <div
        className="document-editor h-full"
        onClick={() => {
          if (onInitialClick && !editor?.isFocused) {
            const selection = window.getSelection();
            const hasSelection = selection
              ? selection.toString().length > 0
              : false;
            if (!hasSelection) {
              onInitialClick();
            }
          }
        }}
      >
        <div className="document-content h-full">
          <RichText
            editor={editor}
            id={doc.id}
            className="min-h-[300px] h-full"
          />
        </div>
      </div>
    );
  },
);

DocumentEditor.displayName = "DocumentEditor";

export default DocumentEditor;
